/**
 * Collections API Hooks
 *
 * Provides React Query hooks for interacting with the Collections edge function.
 * Supports customers, locations, slots, routes planned, and route instances.
 */
import { useMemo } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { addDays, parseISO } from "date-fns"

import { useAuth } from "@/contexts/AuthContext"
import { buildAdhocRouteCode } from "@/lib/adhocRouteCode"
import { supabase } from "@/lib/supabase"
import {
  AvailabilitySlot,
  CollectionsConfig,
  Customer,
  DayOfWeek,
  Depot,
  Location,
  LocationCustomer,
  OperatorAllocation,
  Route,
  RouteInstance,
  RouteKind,
  RoutePlanned,
  RouteStop,
  ShiftExceptionsResult,
  SlotType,
  StopInstance,
  VehicleType,
} from "@/types/collections"

// API types

type EntityType =
  | "customers"
  | "locations"
  | "location_customers"
  | "availability_slots"
  | "routes_planned"
  | "route_stops"
  | "route_instances"
  | "stop_instances"
  | "stop_comments"
  | "route_comments"
  | "route_edit_history"
  | "vehicle_types"
  | "config"
  | "depots"
  | "fleet_units"
  | "fleet_availability"
  | "operators"

interface ListFilters {
  [key: string]: unknown
}

interface Pagination {
  page: number
  pageSize: number
}

interface OrderBy {
  column: string
  ascending: boolean
}

interface CollectionsResponse<T> {
  success: boolean
  data?: T
  error?: string
  count?: number
}

// API helper function

/**
 * The `message` an edge function put in its own error body, when there is one to read.
 *
 * supabase-js reports every non-2xx as the same sentence and hangs the actual Response off
 * `context`.
 */
async function readFunctionErrorMessage(error: unknown): Promise<string | null> {
  const context = (error as { context?: unknown }).context
  if (!(context instanceof Response)) return null
  try {
    const body = (await context.clone().json()) as { message?: unknown }
    return typeof body?.message === "string" && body.message.trim().length > 0
      ? body.message
      : null
  } catch {
    return null
  }
}

/**
 * Call an edge function, and fail loudly on a transport error.
 *
 * `label` is what appears in the thrown message, so a failure names the system
 * that produced it rather than "Edge Function returned a non-2xx status".
 */
async function invokeEdge<T>(
  functionName: string,
  label: string,
  body: object,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(functionName, { body })
  if (error) {
    // The function's own message in preference to the wrapper's.
    const detail = await readFunctionErrorMessage(error)
    throw new Error(detail ?? `${label}: ${error.message}`)
  }
  return data as T
}

/**
 * The same, for the functions that wrap their payload in `{ success, data,
 * error }` -- unwrapped to the payload, or thrown if the function reported a
 * failure of its own.
 */
async function invokeEdgeUnwrapped<T>(
  functionName: string,
  label: string,
  body: object,
): Promise<T> {
  const response = await invokeEdge<CollectionsResponse<T>>(functionName, label, body)
  if (!response.success) {
    throw new Error(response.error || "Unknown error")
  }
  return response.data as T
}

export function collectionsApi<T>(body: Record<string, unknown>): Promise<T> {
  return invokeEdgeUnwrapped<T>("collections", "Collections API error", body)
}

// Mutation helpers

/** The root every collections query key hangs off. */
const COLLECTIONS_ROOT = "collections"

type CollectionsKey = readonly [typeof COLLECTIONS_ROOT, ...string[]]

const key = (...parts: string[]): CollectionsKey => [COLLECTIONS_ROOT, ...parts] as CollectionsKey

/**
 * A stop and the route it belongs to are one thing to the user and two queries to the cache: stops
 * are nested inside the route_instances payload, so a change to either has to invalidate both.
 */
const ROUTE_AND_STOP_INSTANCES: CollectionsKey[] = [
  key("route_instances"),
  key("stop_instances"),
]

/** A mutation that invalidates a fixed set of query keys when it succeeds. */
function useInvalidatingMutation<TData, TVariables>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  invalidate: CollectionsKey[],
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => {
      invalidate.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }))
    },
  })
}

/**
 * False for a partner login.
 *
 * A partner's API is three reads -- a day's routes, the plan, sync freshness -- and its own
 * operator's driver assignment; the functions answer everything else with a 403. So the queries
 * that would be refused are not sent. A screen a partner shares with HIVED then shows what it
 * has, with the costed and cover-checked parts simply absent, instead of an error for data it
 * was never going to be given.
 */
function useFullApi(): boolean {
  return !useAuth().isPartner
}

// Generic CRUD hooks

export function useCollectionsList<T>(
  entity: EntityType,
  filters?: ListFilters,
  pagination?: Pagination,
  orderBy?: OrderBy,
  enabled: boolean = true,
) {
  const fullApi = useFullApi()
  return useQuery({
    queryKey: ["collections", entity, "list", filters, pagination, orderBy],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("collections", {
        body: {
          action: "list",
          entity,
          filters,
          pagination,
          orderBy,
        },
      })

      if (error) throw new Error(error.message)
      const response = data as CollectionsResponse<T[]>
      if (!response.success) throw new Error(response.error)

      return {
        data: response.data || [],
        count: response.count || 0,
      }
    },
    enabled: enabled && fullApi,
  })
}

export function useCollectionsGet<T>(
  entity: EntityType,
  id: string | undefined,
  include?: string[],
) {
  const fullApi = useFullApi()
  return useQuery({
    queryKey: ["collections", entity, "get", id, include],
    queryFn: () =>
      collectionsApi<T>({
        action: "get",
        entity,
        id,
        include,
      }),
    enabled: !!id && fullApi,
  })
}

export function useCollectionsCreate<T>(entity: EntityType) {
  return useInvalidatingMutation(
    (data: Partial<T>) => collectionsApi<T>({ action: "create", entity, data }),
    [key(entity)],
  )
}

export function useCollectionsUpdate<T>(entity: EntityType) {
  return useInvalidatingMutation(
    ({ id, data }: { id: string; data: Partial<T> }) =>
      collectionsApi<T>({ action: "update", entity, id, data }),
    [key(entity)],
  )
}

export function useCollectionsDelete(entity: EntityType) {
  return useInvalidatingMutation(
    (id: string) => collectionsApi<{ deleted: boolean }>({ action: "delete", entity, id }),
    [key(entity)],
  )
}

/**
 * The five CRUD hooks for one entity, bound to its type and table.
 *
 * Each entity had written these out as four or five one-line thunks over the
 * generic hooks above -- the same shape, six times over.
 *
 * `orderBy` is per entity because the default sort is part of what the list
 * means: customers by name, slots by start time, location-customers with the
 * primary one first.
 */
function entityHooks<T>(entity: EntityType, orderBy?: OrderBy, include?: string[]) {
  return {
    useList: (filters?: ListFilters, enabled: boolean = true) =>
      useCollectionsList<T>(entity, filters, undefined, orderBy, enabled),
    useGet: (id: string | undefined) => useCollectionsGet<T>(entity, id, include),
    useCreate: () => useCollectionsCreate<T>(entity),
    useUpdate: () => useCollectionsUpdate<T>(entity),
    useRemove: () => useCollectionsDelete(entity),
  }
}

// Customer hooks

const customerHooks = entityHooks<Customer>("customers", { column: "name", ascending: true })
export const useCustomers = customerHooks.useList
export const useCreateCustomer = customerHooks.useCreate
export const useUpdateCustomer = customerHooks.useUpdate
export const useDeleteCustomer = customerHooks.useRemove

// Location hooks

const locationHooks = entityHooks<Location>(
  "locations",
  { column: "site_name", ascending: true },
  ["customer", "slots"],
)
export const useLocations = locationHooks.useList
export const useLocation = locationHooks.useGet
export const useCreateLocation = locationHooks.useCreate
export const useUpdateLocation = locationHooks.useUpdate
export const useDeleteLocation = locationHooks.useRemove

// Availability slot hooks (Contracted slots at locations)

const availabilitySlotHooks = entityHooks<AvailabilitySlot>(
  "availability_slots",
  { column: "start_time", ascending: true },
  ["location"],
)
export const useAvailabilitySlots = availabilitySlotHooks.useList
export const useCreateAvailabilitySlot = availabilitySlotHooks.useCreate
export const useUpdateAvailabilitySlot = availabilitySlotHooks.useUpdate
export const useDeleteAvailabilitySlot = availabilitySlotHooks.useRemove

// Legacy aliases for backwards compatibility
export const useSlots = useAvailabilitySlots
export const useCreateSlot = useCreateAvailabilitySlot
export const useUpdateSlot = useUpdateAvailabilitySlot
export const useDeleteSlot = useDeleteAvailabilitySlot

// Location-customer hooks (Many-to-many)

export function useCreateLocationCustomer() {
  return useCollectionsCreate<LocationCustomer>("location_customers")
}

export function useDeleteLocationCustomer() {
  return useCollectionsDelete("location_customers")
}

// Route stop hooks (Stops on routes)

export function useCreateRouteStop() {
  return useCollectionsCreate<RouteStop>("route_stops")
}

export function useUpdateRouteStop() {
  return useCollectionsUpdate<RouteStop>("route_stops")
}

export function useDeleteRouteStop() {
  return useCollectionsDelete("route_stops")
}

// Routes planned hooks

export function useRoutesPlanned(filters?: ListFilters, enabled: boolean = true) {
  // Use custom query to include stops with their locations
  return useQuery({
    queryKey: ["collections", "routes_planned", "list_with_stops", filters],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("collections", {
        body: {
          action: "list_routes_planned_with_slots",
          filters,
        },
      })

      if (error) throw new Error(error.message)
      const response = data as {
        success: boolean
        data?: RoutePlanned[]
        error?: string
        count?: number
      }
      if (!response.success) throw new Error(response.error)

      return {
        data: response.data || [],
        count: response.count || 0,
      }
    },
    enabled,
  })
}

export function useRoutePlanned(id: string | undefined) {
  return useCollectionsGet<RoutePlanned>("routes_planned", id)
}

export function useCreateRoutePlanned() {
  return useCollectionsCreate<RoutePlanned>("routes_planned")
}

// Route instance hooks

export function useRouteInstancesForDate(
  date: string,
  depot?: string,
  status?: string[],
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["collections", "route_instances", "for_date", date, depot, status],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("collections", {
        body: {
          action: "get_route_instances_for_date",
          date,
          depot,
          status,
        },
      })

      if (error) throw new Error(error.message)
      const response = data as { success: boolean; data?: RouteInstance[]; error?: string }
      if (!response.success) throw new Error(response.error)

      return response.data || []
    },
    enabled,
    // Refetch every 30 seconds for live updates
    refetchInterval: 30000,
  })
}

// Fetch route instances for a date range (for list view volume display)
// Uses the edge function which has proper auth and RLS handling
export function useRouteInstancesForDateRange(
  startDate: string | undefined,
  endDate: string | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["collections", "route_instances", "for_date_range", startDate, endDate],
    queryFn: async () => {
      if (!startDate || !endDate) return []

      // Use edge function to get route instances (same as useRouteInstancesForDate but for a range)
      // Fetch each date in the range using the working edge function
      const allData: RouteInstance[] = []
      const currentDate = new Date(startDate)
      const endDateObj = new Date(endDate)

      // Limit to 14 days max to avoid too many requests
      const maxDays = 14
      let dayCount = 0

      while (currentDate <= endDateObj && dayCount < maxDays) {
        const dateStr = currentDate.toISOString().split("T")[0]

        try {
          const { data, error } = await supabase.functions.invoke("collections", {
            body: {
              action: "get_route_instances_for_date",
              date: dateStr,
            },
          })

          if (!error && data?.success && data?.data) {
            allData.push(...data.data)
          }
        } catch {
          // Silently continue if a date fails
        }

        currentDate.setDate(currentDate.getDate() + 1)
        dayCount++
      }

      return allData
    },
    enabled: enabled && !!startDate && !!endDate,
    // Cache for 1 minute
    staleTime: 60000,
  })
}

export function useCreateRouteInstance() {
  return useCollectionsCreate<RouteInstance>("route_instances")
}

export function useUpdateRouteInstance() {
  return useCollectionsUpdate<RouteInstance>("route_instances")
}

/**
 * Cancel a route instance.
 *
 * Cancelling sets the status and keeps everything else — the row, its stops, its comments and
 * its edit history all survive, which is what makes the action reversible and auditable. The
 * rest of the app is already built for this: the monitoring views hide cancelled routes unless
 * you ask for them, the Track-POD export skips them, and the unique index on
 * (planned_route_id, date) excludes them so the day can be re-planned.
 *
 * Never delete instead: the reason a route was pulled, and the fact it ever existed, would both
 * be lost.
 *
 * Step 1 is the operation and its failure aborts. Steps 2 and 3 are best-effort: once the
 * status is written the route *is* cancelled, and failing to record the reason or to withdraw
 * it from Track-POD must not leave the caller thinking otherwise.
 */
export function useCancelRouteInstance() {
  return useInvalidatingMutation(
    async ({
      routeInstanceId,
      routeCode,
      reason,
      authorName,
    }: {
      routeInstanceId: string
      routeCode: string
      reason: string
      authorName: string
    }) => {
      // One id across the calls below, so the cancellation and its reason read as one action in
      // the route's history rather than as unrelated rows moments apart.
      const correlationId = crypto.randomUUID()

      const cancelled = await collectionsApi<RouteInstance>({
        action: "update",
        entity: "route_instances",
        id: routeInstanceId,
        data: { status: "cancelled" },
        correlationId,
      })

      try {
        await collectionsApi<{ id: string }>({
          action: "create",
          entity: "route_comments",
          data: {
            route_instance_id: routeInstanceId,
            author_name: authorName,
            content: `[Cancelled ${routeCode}] ${reason.trim()}`,
          },
          correlationId,
        })
      } catch (error) {
        console.error("Route cancelled, but the reason could not be recorded:", error)
      }

      // Best-effort, and it may legitimately decline: a route the driver has started cannot be
      // pulled out of Track-POD, and the backend says so rather than deleting the orders that
      // hold the PODs for stops already served. Either way the cancellation itself stands — the
      // caller is told which happened so it can word the confirmation honestly.
      let trackpodWithdrawn = false
      try {
        const withdrawal = await trackpodSyncApi({ action: "delete_route", routeInstanceId })
        trackpodWithdrawn = withdrawal.success && withdrawal.data?.withdrawn !== false
      } catch (error) {
        console.error("Route cancelled, but Track-POD could not be updated:", error)
      }

      return { ...cancelled, trackpodWithdrawn }
    },
    [key("route_instances"), key("route_comments")],
  )
}

/**
 * Duplicate a route instance as a new ad-hoc route on the same date.
 *
 * Times are copied as times-of-day rather than instants, so a duplicate lands at the same
 * clock times on the target date. A stop whose departure would fall before its arrival has
 * crossed midnight and carries a day offset.
 *
 * Extracted from the timeline toolbar so the route panel can offer the same action on an
 * already-selected route without a second copy of this sequence.
 */
export function useDuplicateRouteInstance() {
  return useInvalidatingMutation(
    async ({
      source,
      dateStr,
      reason,
      authorName,
      customCode,
    }: {
      source: Route
      dateStr: string
      reason: string
      authorName: string
      /** Optional short code the operator typed, e.g. "FUEL1". Blank falls back to digits. */
      customCode?: string
    }) => {
      const routeDate = parseISO(dateStr)
      const stops = source.stops ?? []
      const firstStop = stops[0]
      const lastStop = stops[stops.length - 1]

      const atTimeOfDay = (base: Date, source: Date | undefined, fallbackHour: number) => {
        const next = new Date(base)
        next.setHours(source?.getHours() ?? fallbackHour, source?.getMinutes() ?? 0, 0, 0)
        return next
      }

      const plannedStart = atTimeOfDay(routeDate, firstStop?.plannedArrival, 8)
      let plannedEnd = atTimeOfDay(routeDate, lastStop?.plannedDeparture, 18)
      if (plannedEnd <= plannedStart) plannedEnd = addDays(plannedEnd, 1)

      // One id across the create, every stop, and the reason comment, so duplicating a route
      // reads as one action rather than as sixteen.
      const correlationId = crypto.randomUUID()

      const created = await collectionsApi<RouteInstance>({
        action: "create",
        entity: "route_instances",
        correlationId,
        data: {
          code: buildAdhocRouteCode(customCode),
          date: dateStr,
          planned_start_time: plannedStart.toISOString(),
          planned_end_time: plannedEnd.toISOString(),
          depot: source.depot || "",
          // A copy of a partner's route is still that partner's: their login is scoped by this
          // column, so a copy that defaulted to HIVED would vanish from the board of the very
          // people being asked to run it.
          operator: source.operator || "HIVED",
          is_adhoc: true,
          is_locked: false,
          status: "scheduled",
          delay_minutes: 0,
          total_stops: stops.length,
          completed_stops: 0,
          // The copy has no plan behind it, so a double-manned source would silently become a
          // single-manned duplicate unless the resolved crew size is written down here.
          crew_size: source.crewSize > 1 ? source.crewSize : null,
        },
      })

      if (!created?.id) throw new Error("Failed to create route instance")
      const newRouteId = created.id

      for (let i = 0; i < stops.length; i++) {
        const stop = stops[i]
        const arrivalOnDate = atTimeOfDay(routeDate, stop.plannedArrival, 8)
        let departureOnDate = atTimeOfDay(routeDate, stop.plannedDeparture, 8)
        if (departureOnDate < arrivalOnDate) departureOnDate = addDays(departureOnDate, 1)

        await collectionsApi<StopInstance>({
          action: "add_adhoc_stop",
          routeInstanceId: newRouteId,
          correlationId,
          stopData: {
            name: stop.name,
            address: stop.address,
            postcode: stop.postcode,
            lat: stop.lat,
            lng: stop.lng,
            location_id: stop.locationId,
            // `stop.type` is the display kind the timeline renders (collection/depot/fuel), not
            // the slot_type enum the column holds — and the mapping between them is lossy, so the
            // original value is carried through rather than reconstructed.
            stop_type: (stop.slotType as SlotType | undefined) ?? "shipper_collection",
            planned_arrival: arrivalOnDate.toISOString(),
            planned_departure: departureOnDate.toISOString(),
            arrival_day_offset: 0,
            departure_day_offset:
              departureOnDate.getTime() > routeDate.getTime() + 24 * 60 * 60 * 1000 ? 1 : 0,
            service_time_minutes: stop.serviceTimeMinutes,
            sequence: i + 1,
          },
        })
      }

      // Best-effort from here: the route and its stops already exist, so a failed push to an
      // external system must not read as a failed duplication.
      try {
        await samsaraSyncApi({ action: "create_route", routeInstanceId: newRouteId })
      } catch (error) {
        console.error("Route duplicated, but Samsara could not be updated:", error)
      }

      try {
        await trackpodSyncApi({ action: "create_route", routeInstanceId: newRouteId })
      } catch (error) {
        console.error("Route duplicated, but Track-POD could not be updated:", error)
      }

      try {
        await collectionsApi<{ id: string }>({
          action: "create",
          entity: "route_comments",
          correlationId,
          data: {
            route_instance_id: newRouteId,
            author_name: authorName,
            content: `[Duplicate of ${source.code}] ${reason.trim()}`,
          },
        })
      } catch (error) {
        console.error("Route duplicated, but the reason could not be recorded:", error)
      }

      return { id: newRouteId, stopCount: stops.length, sourceCode: source.code }
    },
    [key("route_instances"), key("stop_instances"), key("route_comments")],
  )
}

// Stop instance hooks

export function useCreateStopInstance() {
  return useCollectionsCreate<StopInstance>("stop_instances")
}

export function useUpdateStopInstance() {
  return useInvalidatingMutation(
    ({ id, data }: { id: string; data: Partial<StopInstance> }) =>
      collectionsApi<StopInstance>({ action: "update", entity: "stop_instances", id, data }),
    ROUTE_AND_STOP_INSTANCES,
  )
}

export function useDeleteStopInstance() {
  return useCollectionsDelete("stop_instances")
}

// Special operations

/**
 * The stop an ad-hoc add writes.
 *
 * Named and exported so a caller can build the payload when the user asks for the stop and hold
 * onto it until they save, rather than only ever at the moment of the write.
 */
export interface AdHocStopData {
  name: string
  address: string
  postcode: string
  lat: number
  lng: number
  location_id?: string
  stop_type: string
  planned_arrival: string
  planned_departure: string
  arrival_day_offset?: number
  departure_day_offset?: number
  service_time_minutes: number
  sequence?: number
  slot_services?: string[]
  slot_pnl?: string[]
}

export function useAddAdHocStop() {
  return useInvalidatingMutation(
    ({ routeInstanceId, stopData }: { routeInstanceId: string; stopData: AdHocStopData }) =>
      collectionsApi<StopInstance>({ action: "add_adhoc_stop", routeInstanceId, stopData }),
    ROUTE_AND_STOP_INSTANCES,
  )
}

// Restore a retired planned route

export interface RestorePlannedRouteResult {
  message: string
  /** Cancelled instances removed to free the day up for regeneration. */
  instancesCleared: number
  /** Instances that now exist for dates that had none. */
  instancesCreated: number
  effectiveFrom: string
}

/**
 * Bring a retired planned route back.
 *
 * Retiring a route -- cancelling it, or giving it an end date -- has never destroyed anything: the
 * template row and its stops survive, which is what makes this a revive rather than a rebuild. The
 * counterpart to useCancelRouteInstance's reasoning one level up, at the template rather than the
 * instance.
 *
 * Forward-only, and deliberately so. Instances cancelled while the route was dead stay cancelled,
 * so the days nothing ran remain the record that nothing ran. Anything a planner edited by hand,
 * pushed to Samsara, or actually ran is left untouched whatever its status.
 */
export function useRestorePlannedRoute() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      routeId,
      effectiveFrom,
      days,
    }: {
      routeId: string
      /** The date the route starts running again. Defaults to server today. */
      effectiveFrom?: string // YYYY-MM-DD
      /** The days it should run. Omit to keep the schedule it had. */
      days?: DayOfWeek[]
    }) =>
      collectionsApi<RestorePlannedRouteResult>({
        action: "restore_planned_route",
        routeId,
        effectiveFrom,
        days,
      }),
    onSuccess: () => {
      // The route itself changed status, and its instances and their stops were regenerated.
      queryClient.invalidateQueries({ queryKey: ["collections", "routes_planned"], exact: false })
      queryClient.invalidateQueries({ queryKey: ["collections", "route_stops"], exact: false })
      queryClient.invalidateQueries({ queryKey: ["collections", "route_instances"], exact: false })
      queryClient.invalidateQueries({ queryKey: ["collections", "stop_instances"], exact: false })
    },
  })
}

// Sync route stops (Atomic replace operation)

interface RouteStopData {
  location_id: string
  sequence: number
  planned_arrival: string // HH:mm format
  planned_departure: string
  arrival_day_offset?: number
  departure_day_offset?: number
  service_time_minutes: number
  stop_type: string
  stop_services?: string[]
  stop_pnl?: string[]
  availability_slot_id?: string
  notes?: string
  /** Optional revenue in GBP when stop_type is haulage_delivery. */
  haulage_delivery_revenue_gbp?: number | null
  /** Agreed price in GBP when stop_type is direct_injection. Prices the whole route it sits on. */
  direct_injection_cost_gbp?: number | null
}

// Save planned route (single round-trip: create/update + stops + optional cascade)

export interface SavePlannedRoutePayload {
  routeId?: string // Omit for create, set for update
  route: {
    code: string
    name: string
    category?: string
    days: string[]
    default_start_time: string
    default_end_time: string
    depot: string
    operator: string
    vehicle_type?: string
    /** Drivers the route is run with; 2 is double-manned. Omit to leave unchanged. */
    crew_size?: number
    /** operational | standby | external. Omit to leave unchanged. */
    route_kind?: RouteKind
    estimated_cost?: number
    status: string
    effective_from: string
    effective_to?: string
    excluded_dates?: string[]
  }
  stops: RouteStopData[]
  cascade?: boolean // For update only; default true
  fromDate?: string // YYYY-MM-DD; cascade from this date (default: server today)
  /**
   * When a code change starts applying to instances already generated, as a Monday.
   *
   * Omit to rename the template only. Instances inside the locked week, already pushed to
   * Track-POD, or already run are never renamed whatever this says.
   */
  renameFromDate?: string // YYYY-MM-DD
}

interface SavePlannedRouteResult {
  routeId: string
  cascade?: unknown
  /** Set when route/stops saved but cascading to future instances failed */
  cascadeError?: string
  /** Present when a code change was carried onto existing instances. */
  rename?: {
    from: string | null
    instancesRenamed: number
    instancesSkipped: number
  }
}

/**
 * Single-call save: create or update planned route, sync stops, and optionally
 * cascade to future instances. Use this instead of create + bulkCreate or
 * update + syncRouteStops + cascade for better performance.
 */
export function useSavePlannedRoute() {
  return useInvalidatingMutation(
    (payload: SavePlannedRoutePayload) =>
      collectionsApi<SavePlannedRouteResult>({
        action: "save_planned_route",
        routeId: payload.routeId,
        route: payload.route,
        stops: payload.stops,
        cascade: payload.cascade,
        fromDate: payload.fromDate,
        // Without this, rename_route_instances is never reached: renaming a plan would leave
        // every instance already generated on the old code.
        renameFromDate: payload.renameFromDate,
      }),
    [key("routes_planned"), key("route_stops"), key("route_instances")],
  )
}

// Weekly plan snapshot hooks

export interface WeeklyPlanSnapshotDayCost {
  labour_cost: number
  charging_cost: number
  vehicle_cost: number
  haulage_revenue: number
  total_cost: number
}

export interface WeeklyPlanSnapshotRoute {
  route_id: string
  code: string
  category: string
  vehicle_type: string
  status: string
  days: Record<string, WeeklyPlanSnapshotDayCost>
}

export interface WeeklyPlanSnapshotDailySummary {
  route_count: number
  labour_cost: number
  charging_cost: number
  vehicle_cost: number
  haulage_revenue: number
  total_cost: number
}

export interface WeeklyPlanSnapshotData {
  routes: WeeklyPlanSnapshotRoute[]
  daily_summaries: Record<string, WeeklyPlanSnapshotDailySummary>
  week_totals: WeeklyPlanSnapshotDailySummary
}

export interface WeeklyPlanSnapshot {
  id: string
  week_start: string
  locked_at: string
  snapshot: WeeklyPlanSnapshotData
  created_at: string
}

export function useWeeklyPlanSnapshot(weekStart: string | undefined) {
  const fullApi = useFullApi()
  return useQuery({
    queryKey: ["collections", "weekly_plan_snapshot", weekStart],
    queryFn: async () => {
      if (!weekStart) return null
      const result = await collectionsApi<WeeklyPlanSnapshot | null>({
        action: "get_weekly_plan_snapshot",
        week_start: weekStart,
      })
      return result
    },
    enabled: !!weekStart && fullApi,
    staleTime: 5 * 60 * 1000,
  })
}

// Bulk create hook

interface BulkCreateEntity {
  entity: EntityType
  data: Record<string, unknown>
}

interface BulkCreateResult {
  entity: EntityType
  success: boolean
  data?: unknown
  error?: string
}

export function useBulkCreate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (entities: BulkCreateEntity[]) =>
      collectionsApi<BulkCreateResult[]>({
        action: "bulk_create",
        entities,
      }),
    onSuccess: () => {
      // Invalidate all collections queries
      queryClient.invalidateQueries({ queryKey: ["collections"] })
    },
  })
}

// Bulk update hooks

interface BulkUpdateItem {
  id: string
  data: Record<string, unknown>
}

interface BulkUpdateResult {
  results: Array<{
    id: string
    success: boolean
    data?: unknown
    error?: string
  }>
  successCount: number
  totalCount: number
  updatedRecords: unknown[]
}

export function useBulkUpdate(entity: EntityType) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (updates: BulkUpdateItem[]) =>
      collectionsApi<BulkUpdateResult>({
        action: "bulk_update",
        entity,
        updates,
      }),
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["collections", entity] })
    },
  })
}

// Convenience hook specifically for bulk updating stop instances
export function useBulkUpdateStopInstances() {
  return useBulkUpdate("stop_instances")
}

// Samsara sync hooks

interface SamsaraCreateRouteRequest {
  action: "create_route"
  routeInstanceId: string
  /** Recorded on the export audit row so timeline saves are distinguishable from cron pushes. */
  trigger?: "ui"
}

interface SamsaraUpdateRouteRequest {
  action: "update_route"
  routeInstanceId: string
  trigger?: "ui"
}

interface SamsaraFetchLiveDataRequest {
  action: "fetch_live_data"
  routeInstanceIds?: string[]
}

interface SamsaraCreateHubLocationRequest {
  action: "create_hub_location"
  name: string
  formattedAddress: string
}

type SamsaraSyncRequest =
  | SamsaraCreateRouteRequest
  | SamsaraUpdateRouteRequest
  | SamsaraFetchLiveDataRequest
  | SamsaraCreateHubLocationRequest

interface SamsaraSyncResponse {
  success: boolean
  message: string
  data?: Record<string, unknown>
  errors?: string[]
}

function samsaraSyncApi(body: SamsaraSyncRequest): Promise<SamsaraSyncResponse> {
  return invokeEdge<SamsaraSyncResponse>("collections-samsara-sync", "Samsara sync error", body)
}

/**
 * Hook to create a route in Samsara GPS
 * Call this after creating an ad-hoc route instance
 */
export function useSamsaraCreateRoute() {
  return useInvalidatingMutation(
    // Invalidates route instances to pick up the new samsara_route_id.
    (routeInstanceId: string) =>
      samsaraSyncApi({ action: "create_route", routeInstanceId, trigger: "ui" }),
    [key("route_instances")],
  )
}

/**
 * Hook to update a route in Samsara GPS
 * Call this after editing stops (reorder, add, edit times)
 */
export function useSamsaraUpdateRoute() {
  return useInvalidatingMutation(
    (routeInstanceId: string) =>
      samsaraSyncApi({ action: "update_route", routeInstanceId, trigger: "ui" }),
    ROUTE_AND_STOP_INSTANCES,
  )
}

/** Response shape from create_hub_location (Samsara address created, id + geocoded lat/lon) */
export interface SamsaraCreateHubLocationResult {
  locationId: string
  latitude: number | null
  longitude: number | null
}

/**
 * Hook to create a location/address in Samsara and get back the location ID and geocoded lat/lon.
 * Use when creating a new directory location so the user doesn't have to enter coordinates.
 * Requires address (and optionally name) to be filled; Samsara geocodes and returns id + lat/lon.
 */
export function useSamsaraCreateHubLocation() {
  return useMutation({
    mutationFn: async ({
      name,
      formattedAddress,
    }: {
      name: string
      formattedAddress: string
    }): Promise<SamsaraCreateHubLocationResult> => {
      const res = await samsaraSyncApi({
        action: "create_hub_location",
        name: name || "Location",
        formattedAddress,
      })
      const data = res.data as
        | {
            locationId?: string
            latitude?: number | string | null
            longitude?: number | string | null
          }
        | undefined
      if (!data?.locationId) {
        throw new Error(res.message || "Samsara did not return a location ID")
      }
      const latRaw = data.latitude != null ? Number(data.latitude) : null
      const lngRaw = data.longitude != null ? Number(data.longitude) : null

      let latitude = latRaw != null && Number.isFinite(latRaw) ? latRaw : null
      let longitude = lngRaw != null && Number.isFinite(lngRaw) ? lngRaw : null

      // Extra safeguard: treat 0,0 as "no coords" for this workflow, since Samsara HQ-style
      // addresses will never legitimately be at (0,0) for HIVED.
      if (latitude === 0 && longitude === 0) {
        latitude = null
        longitude = null
      }

      return {
        locationId: data.locationId,
        latitude,
        longitude,
      }
    },
  })
}

// Track-POD sync hooks

interface TrackPodCreateRouteRequest {
  action: "create_route"
  routeInstanceId: string
  /** Recorded on the export audit row so timeline saves are distinguishable from cron pushes. */
  trigger?: "ui"
}

interface TrackPodUpdateRouteRequest {
  action: "update_route"
  routeInstanceId: string
  trigger?: "ui"
}

interface TrackPodDeleteRouteRequest {
  action: "delete_route"
  routeInstanceId: string
}

interface TrackPodIngestStopStatusRequest {
  action: "ingest_stop_status"
  /** Defaults server-side to today. */
  date?: string
  routeInstanceIds?: string[]
  trigger?: "ui"
}

type TrackPodSyncRequest =
  | TrackPodCreateRouteRequest
  | TrackPodUpdateRouteRequest
  | TrackPodDeleteRouteRequest
  | TrackPodIngestStopStatusRequest

interface TrackPodSyncResponse {
  success: boolean
  message: string
  data?: Record<string, unknown>
  errors?: string[]
}

function trackpodSyncApi(body: TrackPodSyncRequest): Promise<TrackPodSyncResponse> {
  return invokeEdge<TrackPodSyncResponse>(
    "collections-trackpod-sync",
    "Track-POD sync error",
    body,
  )
}

/**
 * Hook to create a route in Track-POD
 * Call this after creating an ad-hoc route instance
 */
export function useTrackPodCreateRoute() {
  return useInvalidatingMutation(
    // Invalidates route instances to pick up the new trackpod_route_id.
    (routeInstanceId: string) =>
      trackpodSyncApi({ action: "create_route", routeInstanceId, trigger: "ui" }),
    [key("route_instances")],
  )
}

/**
 * Hook to update a route in Track-POD
 * Call this after editing stops (reorder, add, edit times)
 */
export function useTrackPodUpdateRoute() {
  return useInvalidatingMutation(
    (routeInstanceId: string) =>
      trackpodSyncApi({ action: "update_route", routeInstanceId, trigger: "ui" }),
    ROUTE_AND_STOP_INSTANCES,
  )
}

/** One integration's last successful pull, as recorded in collections_sync_runs. */
export interface SyncRunStatus {
  system: "samsara" | "trackpod"
  kind: "live" | "reconcile"
  status: "success" | "partial"
  started_at: string
  finished_at: string | null
  routes_seen: number
  stops_updated: number
  disagreements: number
}

export interface SyncStatus {
  samsara: SyncRunStatus | null
  trackpod: SyncRunStatus | null
}

/**
 * How fresh each integration's data is.
 *
 * Polled on the same 30s cadence as the routes themselves — a freshness indicator that is itself
 * stale is worse than none, because it actively reassures.
 */
export function useSyncStatus(enabled: boolean = true) {
  return useQuery({
    queryKey: ["collections", "sync_status"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("collections", {
        body: { action: "get_sync_status" },
      })

      if (error) throw new Error(error.message)
      const response = data as { success: boolean; data?: SyncStatus; error?: string }
      if (!response.success) throw new Error(response.error)

      return response.data ?? { samsara: null, trackpod: null }
    },
    refetchInterval: 30000,
    enabled,
  })
}

// Comment hooks

interface RouteCommentData {
  route_instance_id: string
  author_name: string
  content: string
  author_id?: string
}

interface StopCommentData {
  stop_instance_id: string
  author_name: string
  content: string
  author_id?: string
}

interface RouteCommentResponse {
  id: string
  route_instance_id: string
  author_id?: string
  author_name: string
  content: string
  created_at: string
}

interface StopCommentResponse {
  id: string
  stop_instance_id: string
  author_id?: string
  author_name: string
  content: string
  created_at: string
}

/**
 * Hook to create a comment on a route instance
 */
export function useCreateRouteComment() {
  return useInvalidatingMutation(
    // Comments are returned inside the route payload as well as their own query.
    (data: RouteCommentData) =>
      collectionsApi<RouteCommentResponse>({ action: "create", entity: "route_comments", data }),
    [key("route_instances"), key("route_comments")],
  )
}

/**
 * Hook to create a comment on a stop instance
 */
export function useCreateStopComment() {
  return useInvalidatingMutation(
    (data: StopCommentData) =>
      collectionsApi<StopCommentResponse>({ action: "create", entity: "stop_comments", data }),
    [key("route_instances"), key("stop_comments")],
  )
}

/**
 * Hook to get comments for a route instance
 */
export function useRouteComments(routeInstanceId: string | undefined) {
  const fullApi = useFullApi()
  return useQuery({
    queryKey: ["collections", "route_comments", routeInstanceId],
    queryFn: () =>
      collectionsApi<RouteCommentResponse[]>({
        action: "list",
        entity: "route_comments",
        filters: { route_instance_id: routeInstanceId },
        orderBy: { column: "created_at", ascending: true },
      }),
    enabled: !!routeInstanceId && fullApi,
  })
}

/**
 * Hook to get comments for a stop instance
 */
export function useStopComments(stopInstanceId: string | undefined) {
  const fullApi = useFullApi()
  return useQuery({
    queryKey: ["collections", "stop_comments", stopInstanceId],
    queryFn: () =>
      collectionsApi<StopCommentResponse[]>({
        action: "list",
        entity: "stop_comments",
        filters: { stop_instance_id: stopInstanceId },
        orderBy: { column: "created_at", ascending: true },
      }),
    enabled: !!stopInstanceId && fullApi,
  })
}

// Vehicle type hooks

export function useVehicleTypes(enabled: boolean = true) {
  return useCollectionsList<VehicleType>(
    "vehicle_types",
    undefined,
    undefined,
    { column: "name", ascending: true },
    enabled,
  )
}

export function useCreateVehicleType() {
  return useCollectionsCreate<VehicleType>("vehicle_types")
}

export function useUpdateVehicleType() {
  return useCollectionsUpdate<VehicleType>("vehicle_types")
}

export function useDeleteVehicleType() {
  return useCollectionsDelete("vehicle_types")
}

// Collections config hooks

const CONFIG_ID = "00000000-0000-0000-0000-000000000001"

export function useCollectionsConfig() {
  const fullApi = useFullApi()
  return useQuery({
    queryKey: ["collections", "config"],
    enabled: fullApi,
    queryFn: async () => {
      try {
        // Try to get the config via collections API
        const data = await collectionsApi<CollectionsConfig>({
          action: "get",
          entity: "config",
          id: CONFIG_ID,
        })
        return { data }
      } catch (error) {
        // If config doesn't exist, return default
        if (
          error instanceof Error &&
          (error.message.includes("not found") || error.message.includes("PGRST116"))
        ) {
          return {
            data: {
              id: CONFIG_ID,
              driver_wage_rate_per_hour: 0,
              driver_max_hours_per_day: 12,
              haulage_min_empty_pct: 10,
              haulage_min_extended_minutes: 60,
              on_site_charging_cost_per_kwh: 0,
              default_cost_per_kwh: 0,
              oaklands_cost_per_kwh: 0.305,
              internal_charging_range_pct: 70,
              planned_mileage_percentage_add: 0,
            } as CollectionsConfig,
          }
        }
        throw error
      }
    },
  })
}

export function useUpdateCollectionsConfig() {
  return useInvalidatingMutation(
    async (data: Partial<CollectionsConfig>) => {
      // Use update action - the edge function handles upsert for config
      const result = await collectionsApi<CollectionsConfig>({
        action: "update",
        entity: "config",
        id: CONFIG_ID,
        data: {
          ...data,
          updated_at: new Date().toISOString(),
        },
      })
      return { data: result }
    },
    [key("config")],
  )
}

// DRB assignment hooks (Airtable driver/vehicle/trailer assignments)

export interface DrbOption {
  id: string
  name: string
  // Vehicles and trailers only: how a route's assigned plate is matched back to an
  // option here rather than on the server. Absent on drivers, whose option id is
  // already the Airtable record id the route links to.
  registration?: string
  // Drivers only: the business identifier a resourcing shift also carries, which is
  // what lets the list view put a booked shift's person onto a route exactly rather
  // than by matching names across two systems that spell them differently. Optional
  // because the edge function deploys separately from this bundle.
  personId?: string
}

export interface DrbCurrentAssignment {
  airtableRecordId: string
  routeCode: string
  driverName: string | null
  driverId: string | null
  vehicleName: string | null
  vehicleId: string | null
  // Optional, and must stay optional: the edge function deploys separately from this
  // bundle, so a frontend that knows about trailers can be talking to a function that
  // does not. Reading these as though they were guaranteed takes the whole panel down.
  trailerName?: string | null
  trailerId?: string | null
}

interface DrbOptionsResponse {
  drivers: DrbOption[]
  vehicles: DrbOption[]
  trailers?: DrbOption[] // absent until the edge function carrying trailers is deployed
  // Only present when the request carried a routeCode, which is now just the legacy
  // path -- see useDrbOptions.
  currentAssignment?: DrbCurrentAssignment | null
}

/**
 * One route's assignment as the function reports it: a driver record id, and plates
 * for the vehicle and trailer. Matched to options here rather than server-side, which
 * is what lets that request skip the option lists entirely.
 */
interface DrbAssignmentResponse {
  currentAssignment: {
    airtableRecordId: string
    routeCode: string
    driverRecordId: string | null
    vehicleRegistration: string | null
    trailerRegistration: string | null
  } | null
}

function drbAssignmentsApi<T>(body: Record<string, unknown>): Promise<T> {
  return invokeEdgeUnwrapped<T>(
    "collections-drb-assignments",
    "DRB Assignments API error",
    body,
  )
}

/** Match a plate off a route to the option carrying it. Cased loosely: the plate and the
 *  option come from different Airtable bases, so their casing need not agree. */
function findByRegistration(
  options: DrbOption[] | undefined,
  registration: string | null | undefined,
): DrbOption | undefined {
  const plate = registration?.trim().toUpperCase()
  if (!plate || !options) return undefined
  return options.find((o) => o.registration?.trim().toUpperCase() === plate)
}

/**
 * DRB driver/vehicle/trailer options plus the current assignment for one route.
 *
 * Two queries behind one hook, because the halves have very different lifetimes. The
 * option lists are identical for every route, so they are fetched once under a
 * route-independent key and reused for every sheet opened afterwards -- keying them by route
 * code would re-read the whole fleet and the driver CRM each time you opened a different
 * route. Only the assignment is per-route, and that is one Airtable request whose
 * registrations are matched to options here.
 *
 * Callers see one shape whichever path answered, so the panel does not care which it got.
 */
export function useDrbOptions(routeCode: string | undefined, enabled: boolean = true) {
  const options = useQuery({
    // No route code: this is the same answer for every route.
    queryKey: ["drb-assignments", "options"],
    queryFn: () => drbAssignmentsApi<DrbOptionsResponse>({ action: "get_options" }),
    enabled: !!routeCode && enabled,
    // Ten minutes, not longer: going stale costs nothing visible, because React Query hands
    // back the cached lists straight away and revalidates behind them. It only bounds how
    // long a vehicle onboarded mid-shift stays missing from the picker.
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1,
  })

  // The edge function deploys separately from this bundle, so until the split above is
  // deployed the options call is rejected for having no routeCode. Rather than drop the
  // pickers for a deploy cycle, fall back to the one call the old function does answer,
  // which returns the lists and the assignment together. Delete this query, and the
  // branch it feeds below, once the function is live.
  const isLegacyFunction = options.isError
  const legacy = useQuery({
    queryKey: ["drb-assignments", "legacy", routeCode],
    queryFn: () => drbAssignmentsApi<DrbOptionsResponse>({ action: "get_options", routeCode }),
    enabled: isLegacyFunction && !!routeCode && enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  const assignment = useQuery({
    queryKey: ["drb-assignments", "assignment", routeCode],
    queryFn: () =>
      drbAssignmentsApi<DrbAssignmentResponse>({ action: "get_assignment", routeCode }),
    enabled: !isLegacyFunction && !!routeCode && enabled,
    // Short, because this is the half someone else can change under you.
    staleTime: 60 * 1000,
    retry: 1,
  })

  const data = useMemo((): DrbOptionsResponse | undefined => {
    if (isLegacyFunction) return legacy.data
    if (!options.data || assignment.isPending) return undefined

    const raw = assignment.data?.currentAssignment
    const driver = raw?.driverRecordId
      ? options.data.drivers.find((d) => d.id === raw.driverRecordId)
      : undefined
    const vehicle = findByRegistration(options.data.vehicles, raw?.vehicleRegistration)
    const trailer = findByRegistration(options.data.trailers, raw?.trailerRegistration)

    return {
      ...options.data,
      // Null where a plate matches no option, as the server's get_options path reports it:
      // the field then falls back to what the route instance says and ticks nothing.
      currentAssignment: raw
        ? {
            airtableRecordId: raw.airtableRecordId,
            routeCode: raw.routeCode,
            driverId: driver?.id ?? null,
            driverName: driver?.name ?? null,
            vehicleId: vehicle?.id ?? null,
            vehicleName: vehicle?.name ?? null,
            trailerId: trailer?.id ?? null,
            trailerName: trailer?.name ?? null,
          }
        : null,
    }
  }, [isLegacyFunction, legacy.data, options.data, assignment.data, assignment.isPending])

  return {
    data,
    // Held until both halves have answered, so a cached option list never shows a route
    // as unassigned for the moment before its own assignment lands.
    isLoading: isLegacyFunction ? legacy.isPending : options.isPending || assignment.isPending,
    isError: isLegacyFunction ? legacy.isError : options.isError || assignment.isError,
  }
}

/** One row of the list view: a route's assignment, already matched to picker options. */
export interface DrbRowAssignment {
  airtableRecordId: string
  driverId: string | null
  driverName: string | null
  vehicleId: string | null
  vehicleName: string | null
  trailerId: string | null
  trailerName: string | null
}

/**
 * The list view's poll cadence: 30s, plus up to 5s of jitter.
 *
 * Jitter is added rather than centred so the interval is never shorter than the staleTime the
 * cache is keyed on -- a 27s tick against a 30s staleTime would serve cache and refetch nothing.
 */
const DRB_POLL_INTERVAL_MS = 30 * 1000
const DRB_POLL_JITTER_MS = 5 * 1000

/**
 * The option lists plus every route's assignment, for the whole list view.
 *
 * The route sheet's useDrbOptions asks about one route because that is all it shows. The list
 * shows a day, so it shares the cached option lists -- same query key, so opening the list after
 * a route sheet costs nothing -- and takes the assignments in a single bulk call rather than one
 * per row.
 *
 * Registrations are matched to options here, exactly as the single-route hook does, which is what
 * lets the bulk call skip the option lists entirely.
 */
export function useDrbRowAssignments(routeCodes: string[], enabled: boolean = true) {
  // Sorted and joined so the key is stable against row reordering: the list re-sorts on every
  // column click, and an array identity alone would refetch the day each time.
  const codesKey = useMemo(() => [...routeCodes].sort().join("|"), [routeCodes])

  const options = useQuery({
    queryKey: ["drb-assignments", "options"],
    queryFn: () => drbAssignmentsApi<DrbOptionsResponse>({ action: "get_options" }),
    enabled: enabled && routeCodes.length > 0,
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: 1,
  })

  const assignments = useQuery({
    queryKey: ["drb-assignments", "rows", codesKey],
    queryFn: () =>
      drbAssignmentsApi<{ assignments: DrbAssignmentResponse["currentAssignment"][] }>({
        action: "get_assignments",
        routeCodes,
      }),
    enabled: enabled && routeCodes.length > 0,
    /*
     * Thirty seconds, matching the route poll, because this is the half somebody else can
     * change under you and two dispatchers on the same board is the normal case -- a two
     * minute lag on someone else's assignment is long enough to double-book a driver.
     *
     * Affordable because the call is bulk. A day of ~40 routes is one filtered read of the
     * DRB table plus one of Fleety for the distinct trailers: two requests per poll whatever
     * the route count, so 0.07 req/s against an Airtable limit of 5 req/s per base. Even ten
     * dispatchers with the board open sit under a sixth of that budget, and fetchWithRetry
     * already backs off on a 429 if some other consumer of the base spikes.
     *
     * staleTime tracks the interval rather than sitting above it: refetchInterval fires
     * regardless of staleness, so a longer staleTime would only mean a remount trusted cache
     * the timer had already decided was too old to keep.
     *
     * Jittered, because a fixed interval synchronises the room. Dispatchers open the board at
     * the start of a shift, within a minute of each other, and a fixed 30s timer keeps them in
     * that formation all day -- so the per-user cost lands as one spike every 30s rather than
     * spread across it, which is the shape that collides with a background sync. A fresh offset
     * per tick spreads them apart over the first few polls and keeps them apart.
     */
    staleTime: 30 * 1000,
    refetchInterval: () => DRB_POLL_INTERVAL_MS + Math.round(Math.random() * DRB_POLL_JITTER_MS),
    retry: 1,
  })

  const byRouteCode = useMemo(() => {
    const map = new Map<string, DrbRowAssignment>()
    if (!options.data || !assignments.data) return map
    for (const raw of assignments.data.assignments) {
      if (!raw) continue
      const driver = raw.driverRecordId
        ? options.data.drivers.find((d) => d.id === raw.driverRecordId)
        : undefined
      const vehicle = findByRegistration(options.data.vehicles, raw.vehicleRegistration)
      const trailer = findByRegistration(options.data.trailers, raw.trailerRegistration)
      map.set(raw.routeCode, {
        airtableRecordId: raw.airtableRecordId,
        driverId: driver?.id ?? null,
        driverName: driver?.name ?? null,
        vehicleId: vehicle?.id ?? null,
        vehicleName: vehicle?.name ?? null,
        trailerId: trailer?.id ?? null,
        trailerName: trailer?.name ?? null,
      })
    }
    return map
  }, [options.data, assignments.data])

  return {
    drivers: options.data?.drivers ?? [],
    vehicles: options.data?.vehicles ?? [],
    trailers: options.data?.trailers ?? [],
    byRouteCode,
    // The option lists gate this, not the assignments: without them a row cannot render a name
    // for what it holds. Assignments arriving later just fill the cells in.
    isLoading: options.isPending || assignments.isPending,
    isError: options.isError || assignments.isError,
  }
}

/**
 * Mutation to update driver, vehicle and/or trailer assignment in the Airtable DRB table.
 * Trailer is Airtable-only: no column on collections_route_instances holds one, so
 * unlike driver and vehicle it is not mirrored into Postgres.
 */
export function useUpdateDrbAssignment() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: {
      routeCode: string
      airtableRecordId?: string
      routeInstanceId?: string
      driverId?: string | null
      vehicleId?: string | null
      trailerId?: string | null
      /** The mate on a double-manned route. Saved to Postgres only -- DRB has no field. */
      secondDriverId?: string | null
    }) =>
      drbAssignmentsApi<{ success: boolean; updatedFields: Record<string, unknown> }>({
        action: "update_assignment",
        ...params,
      }),
    onSuccess: (_data, variables) => {
      // Only this route's assignment: the option lists are unaffected by a save, and
      // re-reading the fleet and the driver CRM after every pick is what made the
      // pickers feel slow.
      queryClient.invalidateQueries({
        queryKey: ["drb-assignments", "assignment", variables.routeCode],
      })
      queryClient.invalidateQueries({
        queryKey: ["drb-assignments", "legacy", variables.routeCode],
      })
      // Invalidate route/collections data so lists and details show the updated assignment
      if (variables.routeInstanceId) {
        queryClient.invalidateQueries({ queryKey: ["collections"] })
      }
    },
  })
}

// Shift exceptions

/**
 * Where the plan and the booked shifts disagree, over a date window.
 *
 * Each call reaches the resourcing Shifts API through the collections function
 * -- there is no local mirror -- so a fetch here is 5-9 paged requests against
 * someone else's production system. staleTime therefore matches
 * refetchInterval: without that, the two surfaces plus a remount would each
 * trigger their own round of paging for the same answer.
 *
 * The counts come back from the edge function rather than being recomputed
 * here, so the badge and the panel read the same number -- the
 * unscheduled-slots badge had to be fixed twice for disagreeing with the panel
 * it opens.
 */
export function useShiftExceptions(
  params: { from?: string; to?: string; depot?: string } = {},
  enabled: boolean = true,
) {
  const fullApi = useFullApi()
  return useQuery({
    queryKey: ["collections", "shift_exceptions", params.from, params.to, params.depot],
    queryFn: () =>
      collectionsApi<ShiftExceptionsResult>({
        action: "get_shift_exceptions",
        from: params.from,
        to: params.to,
        depot: params.depot,
      }),
    enabled: enabled && fullApi,
    staleTime: 5 * 60_000,
    refetchInterval: 5 * 60_000,
  })
}

// Operators

/** Who can run a route: HIVED, and the partner companies. Managed from the access screen. */
export function useOperators(enabled: boolean = true) {
  return useCollectionsList<{ name: string }>(
    "operators",
    undefined,
    undefined,
    { column: "name", ascending: true },
    enabled,
  )
}

/** An allocation as the planning screens list it: with its route's code and who made it. */
export interface OperatorAllocationRow extends OperatorAllocation {
  operator: string
  planned_route_id: string
  route_code: string | null
  created_at: string
  created_by: string | null
}

/** Allocations not yet over, for one route or all of them. */
export function useOperatorAllocations(routeId?: string, enabled: boolean = true) {
  const fullApi = useFullApi()
  return useQuery({
    queryKey: ["collections", "operator_allocations", routeId ?? "all"],
    queryFn: () =>
      collectionsApi<OperatorAllocationRow[]>({ action: "list_operator_allocations", routeId }),
    enabled: enabled && fullApi,
  })
}

/** An allocation changes who runs each day in it, so the plan and the days refetch too. */
const OPERATOR_ALLOCATION_KEYS: CollectionsKey[] = [
  key("operator_allocations"),
  key("routes_planned"),
  ...ROUTE_AND_STOP_INSTANCES,
]

export function useAllocateOperator() {
  return useInvalidatingMutation(
    (variables: {
      routeIds: string[]
      operator: string
      from: string
      to: string
      note?: string | null
    }) =>
      collectionsApi<OperatorAllocationRow[]>({ action: "allocate_operator", ...variables }),
    OPERATOR_ALLOCATION_KEYS,
  )
}

export function useRemoveOperatorAllocation() {
  return useInvalidatingMutation(
    (id: string) =>
      collectionsApi<OperatorAllocationRow[]>({ action: "remove_operator_allocation", id }),
    OPERATOR_ALLOCATION_KEYS,
  )
}

// Depots

export function useDepots(enabled: boolean = true) {
  return useCollectionsList<Depot>(
    "depots",
    undefined,
    undefined,
    { column: "name", ascending: true },
    enabled,
  )
}

